import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserMinus } from 'lucide-react';
import {
  adminGetCompany,
  adminUpdateCompany,
  adminAddCompanyMember,
  adminSetCompanyMemberRole,
  adminRemoveCompanyMember,
} from '../../lib/adminApi';
import type { AdminCompanyDetail, AdminCompanyMemberRow, CompanyRole, CompanyStatus } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { describeMemberError, readApiFieldErrors } from './companyErrorCopy';
import type { MemberErrorView } from './companyErrorCopy';

/*
 * Velaigal /admin/companies/:id — one tenant.
 *
 * Company fields (name / website / description / status) plus the member table, which is the
 * only way a recruiter gets a seat: there is no self-serve company creation and no invite
 * flow in v1. Removal here is a status flip server-side, never a row delete, so a removed
 * recruiter stays listed — their past decisions must keep resolving to a real person.
 *
 * Every failure on this page therefore has to be *readable*, because there is no other screen
 * to try instead. Two consequences show up all over the JSX below:
 *
 *   1. Member mutations render a persistent inline alert (ErrorAlert), not only a toast. The
 *      server's own sentence says what went wrong; describeMemberError (./companyErrorCopy)
 *      adds what to do about it — keyed on the code AND on which of the three member routes
 *      failed, because role_mismatch / conflict / last_owner have three different fixes and
 *      because a 404 means "no such account" on attach but "the seat vanished" on role/remove.
 *      Each call below therefore passes its own action. The toast is kept for immediacy; the
 *      alert is what is still on screen when the admin goes looking for the reason.
 *   2. A 400 validation_failed arrives with the message "Invalid input." and the real detail in
 *      `fields` (server/src/app.ts:77). Rendered against the named input ONLY where the form can
 *      actually produce one — that is AddMemberForm's email box and nothing else on this page;
 *      see the notes in both catch blocks.
 *
 * Suspension is one-way and the copy below says so out loud: active -> suspended closes the
 * company's published jobs in the same transaction (server/src/routes/adminCompanies.ts), and
 * re-activating reopens none of them.
 */

const NEUTRAL_CHIP_STYLE = { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' } as const;

const MEMBER_STATUS_LABEL: Record<AdminCompanyMemberRow['status'], string> = {
  invited: 'Invited',
  active: 'Active',
  suspended: 'Suspended',
  removed: 'Removed',
};

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this company.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">This company isn&apos;t available.</h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      No company has this id — check the link you followed. Companies are never deleted, only suspended.
    </p>
    <Link to="/admin/companies" className="btn-primary">
      Back to companies
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">Couldn&apos;t load this company.</h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

/*
 * The persistent half of a failed member mutation. `message` is the server's sentence — why it
 * failed; `hint` is the remedy, and the three 409 codes each get their own (see
 * ./companyErrorCopy). Tinted with the same rgba(153, 27, 27, …) wash over --color-error that
 * src/pages/JobSeeker/Resume.tsx uses — flat fill and a hairline, no shadow, no gradient.
 */
const ErrorAlert: React.FC<{ view: MemberErrorView }> = ({ view }) => (
  <div
    role="alert"
    className="border rounded-md p-3 mb-4"
    style={{ borderColor: 'var(--color-error)', backgroundColor: 'rgba(153, 27, 27, 0.08)' }}
  >
    <p className="text-sm font-medium" style={{ color: 'var(--color-error)' }}>
      {view.message}
    </p>
    {view.hint && <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{view.hint}</p>}
  </div>
);

// tone 'cream' is for spinners inside dark-filled buttons (.btn-primary, .btn-danger);
// tone 'ink' is for spinners inside .btn-secondary, whose background is paper — cream on
// cream would be invisible. Mirrors src/pages/Admin/UserDetail.tsx.
const InlineSpinner: React.FC<{ label: string; tone?: 'cream' | 'ink' }> = ({ label, tone = 'cream' }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={
        tone === 'ink'
          ? { borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }
          : { borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }
      }
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const FieldError: React.FC<{ message: string | undefined }> = ({ message }) =>
  message ? (
    <p role="alert" className="text-xs text-error font-medium mt-1">
      {message}
    </p>
  ) : null;

interface CompanyCardProps {
  detail: AdminCompanyDetail;
  /*
   * Deliberately takes no payload and reloads the whole detail rather than merging the PATCH
   * response into `company`. Suspending closes every published job in the same transaction, so a
   * merge that touched only `company` left the Activity card next door still showing the old
   * `Published: N` — the page would say the listings went down and then show them up.
   */
  onSaved: () => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ detail, onSaved }) => {
  const { company } = detail;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(company.name);
  const [website, setWebsite] = useState(company.website);
  const [description, setDescription] = useState(company.description);
  const [status, setStatus] = useState<CompanyStatus>(company.status);

  // True only for the transition that actually takes listings down. Re-picking 'suspended' on an
  // already-suspended company closes nothing new, so it gets the milder past-tense note instead.
  const suspending = status === 'suspended' && company.status === 'active';

  const startEditing = () => {
    setName(company.name);
    setWebsite(company.website);
    setDescription(company.description);
    setStatus(company.status);
    setEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await adminUpdateCompany(company.id, {
        name: name.trim(),
        website: website.trim(),
        description: description.trim(),
        status,
      });
      onSaved();
      toast.success('Company updated.');
      setEditing(false);
    } catch (err) {
      /*
       * No per-field rendering here, deliberately — and for the same reason Task 409 declined it
       * on CreatePanel. This form cannot produce a validation_failed: `name` is `required` and
       * the submit is disabled on `!name.trim()`, `status` is a two-option select, and every
       * input's maxLength (200 / 300 / 4000) equals its zod bound in updateCompanySchema, so
       * nothing it can send is out of range. The email input in AddMemberForm is different and
       * does keep its field error — see the note there.
       */
      toast.error(err instanceof ApiError ? err.message : 'Could not update this company.');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="card mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <span className="label-editorial">Company</span>
          <button type="button" className="btn-secondary" style={{ padding: '0.375rem 0.875rem' }} onClick={startEditing}>
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className="label-editorial block mb-1">Slug</span>
            <p className="text-sm text-ink">{company.slug}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Status</span>
            <p className="text-sm text-ink">{company.status === 'active' ? 'Active' : 'Suspended'}</p>
            {company.status === 'suspended' && (
              <p className="text-xs text-ink-tertiary mt-1 leading-relaxed">
                Its published jobs were closed when it was suspended. Switching back to Active reopens none of them —
                each has to be published again.
              </p>
            )}
          </div>
          <div>
            <span className="label-editorial block mb-1">Website</span>
            <p className="text-sm text-ink break-words">{company.website || '—'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Evidence retention</span>
            <p className="text-sm text-ink">{company.evidenceRetentionDays} days</p>
          </div>
          <div className="sm:col-span-2">
            <span className="label-editorial block mb-1">Description</span>
            <p className="text-sm text-ink whitespace-pre-line">{company.description || '—'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Added</span>
            <p className="text-sm text-ink">{formatRelativeDate(company.createdAt)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="card mb-6">
      <span className="label-editorial block mb-4">Edit company</span>
      <div className="space-y-4">
        <div>
          <label className="label-editorial block mb-1" htmlFor="edit-name">
            Name
          </label>
          <input
            id="edit-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div>
          <label className="label-editorial block mb-1" htmlFor="edit-website">
            Website
          </label>
          <input
            id="edit-website"
            className="input"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            maxLength={300}
          />
        </div>
        <div>
          <label className="label-editorial block mb-1" htmlFor="edit-description">
            Description
          </label>
          <textarea
            id="edit-description"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={4000}
          />
        </div>
        <div>
          <label className="label-editorial block mb-1" htmlFor="edit-status">
            Status
          </label>
          <select
            id="edit-status"
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as CompanyStatus)}
            style={{ maxWidth: 240 }}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          {/*
            * Shown at the moment the admin picks it, above the button that commits it. Suspension
            * is not a pause: it closes the tenant's published jobs in the same transaction and
            * re-activating reopens nothing, so the UI must never read as reversible.
            */}
          {suspending && (
            <p role="alert" className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--color-error)' }}>
              This is one-way. Saving closes every published job at this company in the same write, and switching back
              to Active later reopens none of them — a recruiter has to publish each one again.
            </p>
          )}
          {!suspending && status === 'suspended' && (
            <p className="text-xs text-ink-tertiary mt-2 leading-relaxed">
              Already suspended. Its published jobs were closed then; switching back to Active reopens none of them.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-6">
        <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
          {busy ? (
            <InlineSpinner label="Saving…" />
          ) : suspending ? (
            'Suspend and close published jobs'
          ) : (
            'Save changes'
          )}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
};

interface MemberTableProps {
  companyId: string;
  members: AdminCompanyMemberRow[];
  onChanged: () => void;
}

const MemberTable: React.FC<MemberTableProps> = ({ companyId, members, onChanged }) => {
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const [error, setError] = useState<MemberErrorView | null>(null);

  const handleRole = async (userId: string, companyRole: CompanyRole) => {
    if (busyUserId) return;
    setBusyUserId(userId);
    setError(null);
    try {
      await adminSetCompanyMemberRole(companyId, userId, companyRole);
      toast.success(companyRole === 'owner' ? 'Promoted to owner.' : 'Changed to member.');
      onChanged();
    } catch (err) {
      // The <select> is controlled off props, so a failed change snaps back on the next render
      // and leaves no trace of itself. Without this alert, demoting the last owner would look
      // like a click that simply did nothing.
      setError(describeMemberError(err, 'role', 'Could not change that role.'));
      toast.error(err instanceof ApiError ? err.message : 'Could not change that role.');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async (userId: string) => {
    if (busyUserId) return;
    setBusyUserId(userId);
    setError(null);
    try {
      await adminRemoveCompanyMember(companyId, userId);
      toast.success('Recruiter removed from this company.');
      setConfirmingUserId(null);
      onChanged();
    } catch (err) {
      setError(describeMemberError(err, 'remove', 'Could not remove that recruiter.'));
      toast.error(err instanceof ApiError ? err.message : 'Could not remove that recruiter.');
      setConfirmingUserId(null);
    } finally {
      setBusyUserId(null);
    }
  };

  if (members.length === 0) {
    return <p className="text-sm text-ink-secondary">No recruiters attached yet.</p>;
  }

  return (
    <>
      {error && <ErrorAlert view={error} />}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-hairline">
              <th className="label-editorial py-2 pr-4 font-medium">Recruiter</th>
              <th className="label-editorial py-2 pr-4 font-medium">Role</th>
              <th className="label-editorial py-2 pr-4 font-medium">Status</th>
              <th className="label-editorial py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const removed = m.status === 'removed';
              return (
                <tr key={m.userId} className="border-b border-hairline last:border-0 align-top">
                  <td className="py-3 pr-4">
                    <p className="text-sm text-ink">{m.name}</p>
                    <p className="text-xs text-ink-secondary">{m.email}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      className="input"
                      style={{ padding: '0.375rem 0.5rem', fontSize: '0.8125rem', maxWidth: 130 }}
                      aria-label={`Role for ${m.email}`}
                      value={m.companyRole}
                      disabled={removed || busyUserId !== null}
                      onChange={(e) => void handleRole(m.userId, e.target.value as CompanyRole)}
                    >
                      <option value="owner">Owner</option>
                      <option value="member">Member</option>
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="chip" style={NEUTRAL_CHIP_STYLE}>
                      {MEMBER_STATUS_LABEL[m.status]}
                    </span>
                    {m.disabled && (
                      <span className="chip ml-2" style={NEUTRAL_CHIP_STYLE}>
                        Account disabled
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {removed ? (
                      <span className="text-xs text-ink-tertiary">
                        {m.removedAt ? `Removed ${formatRelativeDate(m.removedAt)}` : 'Removed'}
                      </span>
                    ) : confirmingUserId === m.userId ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="btn-danger"
                          style={{ padding: '0.375rem 0.875rem' }}
                          onClick={() => void handleRemove(m.userId)}
                          disabled={busyUserId !== null}
                        >
                          {busyUserId === m.userId ? <InlineSpinner label="Removing…" /> : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ padding: '0.375rem 0.875rem' }}
                          onClick={() => setConfirmingUserId(null)}
                          disabled={busyUserId !== null}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '0.375rem 0.875rem' }}
                        onClick={() => setConfirmingUserId(m.userId)}
                        disabled={busyUserId !== null}
                      >
                        <UserMinus className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const AddMemberForm: React.FC<{ companyId: string; onAdded: () => void }> = ({ companyId, onAdded }) => {
  const [email, setEmail] = useState('');
  const [companyRole, setCompanyRole] = useState<CompanyRole>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MemberErrorView | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    setFieldErrors(null);
    try {
      await adminAddCompanyMember(companyId, { email: email.trim(), companyRole });
      toast.success('Recruiter attached.');
      setEmail('');
      setCompanyRole('member');
      onAdded();
    } catch (err) {
      /*
       * Unlike CompanyCard's form, this one CAN produce a validation_failed, which is why it
       * keeps a field error: `type="email"` accepts a bare hostname that zod's `.email()`
       * rejects, so the browser happily submits "a@b" or "foo@bar" and the server answers 400
       * with fields.email. Measured against `z.string().trim().email().max(320)` rather than
       * assumed — both come back rejected.
       *
       * The email also stays in the input on failure: for role_mismatch the admin needs to see
       * which address was wrong, and for conflict they will retry the very same one.
       */
      setFieldErrors(readApiFieldErrors(err));
      setError(describeMemberError(err, 'attach', 'Could not attach that recruiter.'));
      toast.error(err instanceof ApiError ? err.message : 'Could not attach that recruiter.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-hairline">
      <span className="label-editorial block mb-3">Attach a recruiter</span>
      {error && <ErrorAlert view={error} />}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          type="email"
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="recruiter@company.com"
          aria-label="Recruiter email"
          maxLength={320}
          style={{ maxWidth: 280, ...(fieldErrors?.email ? { borderColor: 'var(--color-error)' } : null) }}
          required
        />
        <select
          className="input"
          value={companyRole}
          onChange={(e) => setCompanyRole(e.target.value as CompanyRole)}
          aria-label="Company role"
          style={{ maxWidth: 140 }}
        >
          <option value="member">Member</option>
          <option value="owner">Owner</option>
        </select>
        <button type="submit" className="btn-primary" disabled={busy || !email.trim()}>
          {busy ? <InlineSpinner label="Attaching…" /> : 'Attach'}
        </button>
      </div>
      <FieldError message={fieldErrors?.email} />
      <p className="text-xs text-ink-tertiary mt-2">
        The account must already exist and have verified its email. Recruiter accounts only.
      </p>
    </form>
  );
};

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<AdminCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't setState
  // afterward — mirrors the load-by-id effect in src/pages/Admin/UserDetail.tsx.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await adminGetCompany(id);
        if (cancelled) return;
        setDetail(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (!detail) return null;

  const { company, members, activity } = detail;
  const reload = () => setLoadNonce((n) => n + 1);
  // The directory's memberCount counts status='active' only; this route returns every member
  // regardless of status (both verified in server/src/routes/adminCompanies.ts). The two numbers
  // are supposed to disagree, so state both rather than let the list look wrong from here.
  const activeCount = members.filter((m) => m.status === 'active').length;

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">Company</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-1">{company.name}</h1>
      <p className="text-sm text-ink-secondary mb-8">{company.slug}</p>

      <CompanyCard detail={detail} onSaved={reload} />

      <div className="card mb-6">
        <span className="label-editorial block mb-4">Activity</span>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <span className="label-editorial block mb-1">Jobs</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.jobs}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Published</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.publishedJobs}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Applications</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.applications}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <span className="label-editorial block mb-1">Recruiters</span>
        <p className="text-xs text-ink-tertiary mb-4 leading-relaxed">
          {activeCount} active of {members.length} listed. The directory counts active seats only; removed and
          suspended seats stay listed here so past decisions still resolve to a person.
        </p>
        <MemberTable companyId={company.id} members={members} onChanged={reload} />
        <AddMemberForm companyId={company.id} onAdded={reload} />
      </div>

      <div className="mt-10 pt-8 border-t border-hairline">
        <Link to="/admin/companies" className="btn-secondary">
          Back to companies
        </Link>
      </div>
    </div>
  );
};

export default CompanyDetail;
